import React, { useState, useEffect } from "react";
import { CreditCard, Check, Loader2, Crown, Star } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import { pricingAPI } from "@/lib/api";

export default function Pricing() {
  const { t } = useLang();
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    pricingAPI
      .get()
      .then((r) => setPricing(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCheckout = async (packageId) => {
    setCheckoutLoading(packageId);
    try {
      const res = await pricingAPI.checkout(packageId, window.location.origin);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch {
      // silent
    } finally {
      setCheckoutLoading(null);
    }
  };

  const formatPrice = (price, currency) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: currency || "eur",
    }).format(price);
  };

  // Use packages from API directly (includes lifetime if available)
  const allPackages = pricing?.packages || [];

  const getPeriodLabel = (pkg) => {
    if (pkg.id === "lifetime") return t("uniquePayment");
    if (pkg.id === "yearly") return t("yearly").toLowerCase();
    return t("monthly").toLowerCase();
  };

  const getBadge = (pkg) => {
    if (pkg.id === "lifetime") return t("lifetimeBadge");
    if (pkg.id === "yearly") return "-33%";
    return null;
  };

  return (
    <div className="min-h-screen max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="pricing-page">
      <div className="text-center mb-12">
        <CreditCard className="w-10 h-10 text-amber-500 mx-auto mb-4" />
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-white mb-3">
          {t("pricingTitle")}
        </h1>
        <p className="text-stone-400">{t("pricingSubtitle")}</p>
      </div>

      {/* Current Plan */}
      {user && (
        <div className="mb-10 bg-[#1C1917] border border-white/5 rounded-2xl p-6 text-center">
          <p className="text-sm text-stone-500 mb-1">{t("currentPlan")}</p>
          <div className="flex items-center justify-center gap-2">
            {user.is_premium ? (
              <>
                <Crown className="w-5 h-5 text-amber-500" />
                <span className="text-lg font-semibold text-amber-500">{t("premium")}</span>
              </>
            ) : (
              <span className="text-lg font-semibold text-stone-300">{t("free")}</span>
            )}
          </div>
          {user.is_premium && user.premium_until && (
            <p className="text-xs text-stone-500 mt-1">
              {t("premiumUntil")}: {new Date(user.premium_until).toLocaleDateString()}
            </p>
          )}
          {!user.is_premium && (
            <p className="text-xs text-stone-500 mt-1">
              {t("freeRecipesUsed")}: {user.free_recipes_used} {t("of")} 1
            </p>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : allPackages.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {allPackages.map((pkg) => {
            const isLifetime = pkg.id === "lifetime";
            const isYearly = pkg.id === "yearly";
            const badge = getBadge(pkg);

            return (
              <div
                key={pkg.id}
                data-testid={`pricing-${pkg.id}`}
                className={`rounded-2xl p-8 relative ${
                  isLifetime
                    ? "bg-gradient-to-br from-[#1a1a3e] to-[#2d1b4e] border-2 border-purple-500/50 ring-1 ring-purple-500/20"
                    : isYearly
                    ? "bg-[#1C1917] border-2 border-amber-500/30 ring-1 ring-amber-500/10"
                    : "bg-[#1C1917] border border-white/5"
                }`}
              >
                {badge && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1 ${
                    isLifetime
                      ? "bg-purple-500 text-white"
                      : "bg-amber-500 text-stone-950"
                  }`}>
                    {isLifetime && <Star className="w-3 h-3" />}
                    {badge}
                  </div>
                )}
                <h3 className="font-heading text-xl font-medium text-white mb-1 capitalize">
                  {isLifetime ? t("lifetime") : (t(pkg.name) || pkg.name)}
                </h3>
                {isLifetime && (
                  <p className="text-xs text-purple-300 mb-4">{t("lifetimeSubtext")}</p>
                )}
                <div className={isLifetime ? "mb-6 mt-2" : "mb-6"}>
                  <span className={`text-4xl font-heading font-light ${isLifetime ? "text-purple-200" : "text-white"}`}>
                    {formatPrice(pkg.price, pkg.currency)}
                  </span>
                  <span className="text-stone-500 text-sm">
                    {" "}/{getPeriodLabel(pkg)}
                  </span>
                </div>
                <ul className="space-y-3 mb-8">
                  {pkg.features?.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-stone-300">
                      <Check className={`w-4 h-4 flex-shrink-0 ${isLifetime ? "text-purple-400" : "text-green-500"}`} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleCheckout(pkg.id)}
                  disabled={checkoutLoading === pkg.id || user?.is_premium}
                  data-testid={`checkout-${pkg.id}`}
                  className={`w-full py-3 font-semibold rounded-full transition-all ${
                    user?.is_premium
                      ? "bg-stone-800 text-stone-500 cursor-not-allowed"
                      : isLifetime
                      ? "bg-purple-500 text-white hover:bg-purple-600 hover:shadow-lg hover:shadow-purple-500/20"
                      : isYearly
                      ? "bg-amber-500 text-stone-950 hover:bg-amber-600"
                      : "border border-white/20 text-white hover:bg-white/5"
                  }`}
                >
                  {checkoutLoading === pkg.id ? (
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  ) : user?.is_premium ? (
                    t("currentPlan")
                  ) : (
                    t("subscribe")
                  )}
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
